import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'

const colors = {
  ink: '#06185f',
  muted: '#4c5d86',
  blue: '#0059f6',
  line: '#d9e3f4',
  green: '#27b45b',
}

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('es-AR')
}

const StatusChip = ({ active, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      border: `1px solid ${active ? '#2cbf63' : '#c8d1df'}`,
      bgcolor: active ? 'rgba(39,180,91,0.12)' : '#f7f8fb',
      color: active ? '#12833d' : colors.muted,
      borderRadius: '6px',
      px: 1.2,
      py: 0.35,
      fontSize: 12,
      fontWeight: 800,
      cursor: 'pointer',
      minWidth: 64,
      '&:hover': {
        bgcolor: active ? 'rgba(39,180,91,0.2)' : '#eef3fb',
      },
    }}
  >
    {active ? 'Activo' : 'Inactivo'}
  </Box>
)

const NameChip = ({ children, onClick }) => (
  <Tooltip title="Editar" arrow>
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        border: `1px solid ${colors.blue}`,
        bgcolor: '#fff',
        color: colors.blue,
        borderRadius: '6px',
        px: 1,
        py: 0.35,
        maxWidth: 190,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 800,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'left',
        '&:hover': {
          bgcolor: '#eef5ff',
        },
      }}
    >
      {children}
    </Box>
  </Tooltip>
)

export default function GestionAdminPanel({
  tab,
  search,
  onSearchChange,
  ocultarInactivos,
  onOcultarInactivosChange,
  vendedores,
  vehiculos,
  loading,
  error,
  onCreate,
  onEdit,
  onToggleStatus,
}) {
  const isVendedores = tab === 'vendedores'
  const rows = isVendedores ? vendedores : vehiculos
  const title = isVendedores ? 'Crear vendedor' : 'Crear vehículo'
  const searchPlaceholder = isVendedores
    ? 'Buscar por nombre, mail, teléfono...'
    : 'Buscar por nombre de vehículo...'

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 590,
        height: "76vh",
        borderRadius: '18px',
        border: `1px solid ${colors.line}`,
        bgcolor: 'rgba(255,255,255,0.96)',
        boxShadow: '0 16px 34px rgba(19,39,84,0.08)',
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mb: 2, flexShrink: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: colors.ink, fontSize: 18, fontWeight: 950 }}>
            {isVendedores ? 'Vendedores' : 'Vehículos'}
          </Typography>
          <Typography sx={{ color: colors.muted, fontSize: 12, fontWeight: 700 }}>
            Configuración y estado
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: '7px',
            px: 2,
            minHeight: 37,
            boxShadow: '0 10px 24px rgba(0,89,246,0.22)',
          }}
        >
          {title}
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mb: 2, flexShrink: 0 }}>
        <TextField
          size="small"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#9aabc6' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: '7px',
              fontSize: 12,
              height: 40,
            },
          }}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            minWidth: 150,
            border: '1px solid #dce5f2',
            bgcolor: '#fff',
            borderRadius: '7px',
            px: 1,
            height: 40,
          }}
        >
          <Typography sx={{ color: colors.muted, fontSize: 12, fontWeight: 700 }}>Ocultar inactivos</Typography>
          <Switch size="small" checked={ocultarInactivos} onChange={(event) => onOcultarInactivosChange(event.target.checked)} />
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          border: `1px solid ${colors.line}`,
          borderRadius: '14px',
          bgcolor: '#fff',
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f7f8fb', color: '#1f2937', fontSize: 12, fontWeight: 700, borderColor: '#edf2fa' } }}>
              <TableCell>{isVendedores ? 'Vendedor' : 'Vehículo'}</TableCell>
              <TableCell>{isVendedores ? 'Incorporación' : 'Fecha de carga'}</TableCell>
              <TableCell align="right">Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 5, color: colors.muted }}>
                  No hay registros para mostrar.
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.map((row) => (
              <TableRow key={`${tab}-${row.id}`} hover sx={{ '& td': { borderColor: '#edf2fa', py: 1.2, fontSize: 12 } }}>
                <TableCell>
                  <NameChip onClick={() => onEdit(row)}>
                    {isVendedores ? row.nombre : row.modelo}
                  </NameChip>
                </TableCell>
                <TableCell>{formatDate(row.created_at)}</TableCell>
                <TableCell align="right">
                  <StatusChip active={row.activo !== false} onClick={() => onToggleStatus(row)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}
